import type { ReactNode } from 'react';
import type { CashReceiptRemittanceLink } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatAmount, formatDate } from './cashReceiptFormat';

/** How a link was made, as a reviewer reads it. */
function MethodLabel({ method }: { method: string }): ReactNode {
  switch (method) {
    case 'exact':
      return (
        <T context="how a cash receipt was linked to a remittance: one remittance whose total equals the receipt amount">
          Exact amount
        </T>
      );
    case 'hand_pick':
      return (
        <T context="how a cash receipt was linked to a remittance: a person picked the remittances">
          Hand-picked
        </T>
      );
    case 'bank_memo':
      return (
        <T context="how a cash receipt was linked to a remittance: read from the bank memo sent with the cash">
          Bank memo
        </T>
      );
    case 'group_sum':
      return (
        <T context="how a cash receipt was linked to a remittance: several remittances that sum to the receipt">
          Combined payment
        </T>
      );
    case 'manual':
      return (
        <T context="how a cash receipt was linked to a remittance: linked by an operator or a source that named the receipt">
          Manual
        </T>
      );
    default:
      return method;
  }
}

function LinkRow({
  link,
  onUnlink,
}: {
  link: CashReceiptRemittanceLink;
  onUnlink?: (link: CashReceiptRemittanceLink) => void;
}) {
  const navigate = useNavigate();
  const gt = useGT();
  return (
    <div
      className="rounded-md border bg-muted/30 p-3 space-y-2"
      data-testid="cash-receipt-remittance-link"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {link.statedCustomerName ??
            gt('(No customer stated)', {
              context:
                'placeholder when a linked remittance does not state a customer',
            })}
        </span>
        <Badge variant="secondary" className="text-xs">
          <MethodLabel method={link.method} />
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>
          <T context="linked remittance detail label, the total the remittance states">
            Stated total
          </T>
        </span>
        <span className="font-medium text-foreground">
          {formatAmount(link.statedTotal)}
        </span>
        <span>
          <T context="linked remittance detail label, where the remittance came from">
            Channel
          </T>
        </span>
        <span className="font-medium text-foreground">
          {link.channel ?? '—'}
        </span>
        <span>
          <T context="linked remittance detail label, when the link was made">
            Linked
          </T>
        </span>
        <span className="font-medium text-foreground">
          {formatDate(link.linkedAt)}
        </span>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={() => {
          void navigate({
            to: '/accounts-receivable/remittances/$remittanceId',
            params: { remittanceId: link.remittanceAdviceId },
            viewTransition: true,
          });
        }}
      >
        <T context="AR cash receipt, navigate to a linked remittance">
          View Remittance
        </T>
      </Button>
      {onUnlink && (
        <Button
          variant="outline"
          size="sm"
          className="ml-2 h-7 text-xs"
          onClick={() => onUnlink(link)}
        >
          <T context="button, close one remittance link on a cash receipt">
            Unlink
          </T>
        </Button>
      )}
    </div>
  );
}

/**
 * The remittances linked to a cash receipt (`ar_receipt_remittance_links`).
 * Renders nothing when there are none.
 */
export function CashReceiptRemittanceLinksSection({
  links,
  onUnlink,
}: {
  links: CashReceiptRemittanceLink[];
  onUnlink?: (link: CashReceiptRemittanceLink) => void;
}) {
  if (links.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="cash-receipt-remittance-links">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="AR cash receipt, section heading for remittances linked to this receipt">
          Linked Remittances
        </T>
      </p>
      {links.map((link) => (
        <LinkRow key={link.linkId} link={link} onUnlink={onUnlink} />
      ))}
    </div>
  );
}
